/**
 * Interactive stage for the Talking Heads canvas: renders the scene at the
 * largest size that fits, and lets you select / drag / resize the video
 * boxes and decorations with snapping guides (canvas edges, centre lines and
 * other items).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ThCanvasScene from './ThCanvasScene';
import { applyLayerBox, layerBox, layerLabel } from './bannerPresets';
import { LAYER_META, decorFor, modeLayerKeys } from './thCanvas';

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const MIN_BOX = 40;
const SNAP_PX = 7;

function itemBox(design, sel, W, H) {
  if (!sel) return null;
  if (sel.kind === 'layer') {
    const l = design.layers[sel.id];
    if (!l) return null;
    return { cx: l.x + l.width / 2, cy: l.y + l.height / 2, w: l.width, h: l.height, rot: 0 };
  }
  const d = design.decor.find((x) => x.id === sel.id);
  return d ? layerBox(d, { w: W, h: H }) : null;
}

export function useFitSize(aspect, pad = 0) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const aw = Math.max(0, r.width - pad * 2);
      const ah = Math.max(0, r.height - pad * 2);
      let w = aw;
      let h = w / aspect;
      if (ah > 0 && h > ah) { h = ah; w = h * aspect; }
      w = Math.floor(w);
      h = Math.floor(w / aspect);
      setSize((p) => (p.w === w && p.h === h ? p : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspect, pad]);
  return [ref, size];
}

export default function ThStage({
  design,
  update,
  checkpoint,
  mode,
  W,
  H,
  media,
  selected,
  onSelect,
  snap = true,
  onEditText,
}) {
  const [wrapRef, dims] = useFitSize(W / H, 14);
  const s = dims.w ? dims.w / W : 1;
  const [drag, setDrag] = useState(null); // { guides: {x:[],y:[]} }
  const dragRef = useRef(null);
  const overlayRef = useRef(null);

  const keys = modeLayerKeys(design, mode);
  const items = useMemo(() => {
    const out = [];
    decorFor(design, 'under', mode).forEach((l) => out.push({ kind: 'decor', id: l.id, layer: l }));
    keys.forEach((k) => out.push({ kind: 'layer', id: k }));
    decorFor(design, 'over', mode).forEach((l) => out.push({ kind: 'decor', id: l.id, layer: l }));
    return out;
  }, [design, mode, keys]);

  const writeBox = useCallback((sel, box) => {
    update((d) => {
      if (sel.kind === 'layer') {
        const l = d.layers[sel.id];
        if (!l) return d;
        const width = Math.round(Math.max(MIN_BOX, Math.min(W, box.w)));
        const height = Math.round(Math.max(MIN_BOX, Math.min(H, box.h)));
        const x = Math.round(Math.max(0, Math.min(W - width, box.cx - width / 2)));
        const y = Math.round(Math.max(0, Math.min(H - height, box.cy - height / 2)));
        if (l.x === x && l.y === y && l.width === width && l.height === height) return d;
        return { ...d, layers: { ...d.layers, [sel.id]: { ...l, x, y, width, height } } };
      }
      return {
        ...d,
        decor: d.decor.map((x) => (x.id === sel.id ? applyLayerBox(x, box, { w: W, h: H }) : x)),
      };
    });
  }, [update, W, H]);

  /** Snap candidates from the canvas and every other visible item. */
  const snapLines = useCallback((sel) => {
    const xs = [0, W / 2, W];
    const ys = [0, H / 2, H];
    items.forEach((it) => {
      if (it.kind === sel.kind && it.id === sel.id) return;
      const b = itemBox(design, it, W, H);
      if (!b || Math.abs(b.rot) > 0.5) return;
      xs.push(b.cx - b.w / 2, b.cx, b.cx + b.w / 2);
      ys.push(b.cy - b.h / 2, b.cy, b.cy + b.h / 2);
    });
    return { xs, ys };
  }, [items, design, W, H]);

  const snapAxis = (cands, values, thr) => {
    let best = null;
    values.forEach((v, i) => {
      cands.forEach((c) => {
        const dlt = c - v;
        if (Math.abs(dlt) <= thr && (!best || Math.abs(dlt) < Math.abs(best.d))) best = { d: dlt, line: c, i };
      });
    });
    return best;
  };

  const startDrag = (e, sel, handle = null) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(sel);
    const box = itemBox(design, sel, W, H);
    if (!box) return;
    if (sel.kind === 'decor') {
      const d = design.decor.find((x) => x.id === sel.id);
      if (d?.locked) return;
    }
    checkpoint?.();
    overlayRef.current?.setPointerCapture?.(e.pointerId);
    const layer = sel.kind === 'layer' ? design.layers[sel.id] : null;
    dragRef.current = {
      sel,
      handle,
      start: { x: e.clientX, y: e.clientY },
      box,
      lockAspect: !!layer && layer.frame?.shape === 'circle',
      lines: snap ? snapLines(sel) : null,
    };
    setDrag({ guides: { x: [], y: [] } });
  };

  const onPointerMove = (e) => {
    const st = dragRef.current;
    if (!st) return;
    const dx = (e.clientX - st.start.x) / s;
    const dy = (e.clientY - st.start.y) / s;
    const b0 = st.box;
    const thr = SNAP_PX / s;
    const guides = { x: [], y: [] };
    let next;

    if (!st.handle) {
      let cx = b0.cx + dx;
      let cy = b0.cy + dy;
      if (st.lines && !e.altKey) {
        const sx = snapAxis(st.lines.xs, [cx - b0.w / 2, cx, cx + b0.w / 2], thr);
        if (sx) { cx += sx.d; guides.x.push(sx.line); }
        const sy = snapAxis(st.lines.ys, [cy - b0.h / 2, cy, cy + b0.h / 2], thr);
        if (sy) { cy += sy.d; guides.y.push(sy.line); }
      }
      next = { ...b0, cx, cy };
    } else {
      const h = st.handle;
      const rad = (b0.rot * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      // pointer delta in the box's local axes
      const lx = dx * cos + dy * sin;
      const ly = -dx * sin + dy * cos;
      let w = b0.w;
      let hh = b0.h;
      if (h.includes('e')) w = b0.w + lx;
      if (h.includes('w')) w = b0.w - lx;
      if (h.includes('s')) hh = b0.h + ly;
      if (h.includes('n')) hh = b0.h - ly;
      w = Math.max(MIN_BOX, w);
      hh = Math.max(MIN_BOX, hh);
      if (st.lockAspect || e.shiftKey) {
        const ar = b0.w / b0.h;
        if (h === 'n' || h === 's') w = hh * ar;
        else if (h === 'e' || h === 'w') hh = w / ar;
        else if (w / hh > ar) hh = w / ar; else w = hh * ar;
      }
      // snap the moving edges (unrotated boxes only)
      if (st.lines && !e.altKey && Math.abs(b0.rot) < 0.5 && !(st.lockAspect || e.shiftKey)) {
        const left0 = b0.cx - b0.w / 2;
        const top0 = b0.cy - b0.h / 2;
        if (h.includes('e')) { const sn = snapAxis(st.lines.xs, [left0 + w], thr); if (sn) { w += sn.d; guides.x.push(sn.line); } }
        if (h.includes('w')) { const sn = snapAxis(st.lines.xs, [left0 + b0.w - w], thr); if (sn) { w -= sn.d; guides.x.push(sn.line); } }
        if (h.includes('s')) { const sn = snapAxis(st.lines.ys, [top0 + hh], thr); if (sn) { hh += sn.d; guides.y.push(sn.line); } }
        if (h.includes('n')) { const sn = snapAxis(st.lines.ys, [top0 + b0.h - hh], thr); if (sn) { hh -= sn.d; guides.y.push(sn.line); } }
      }
      // keep the opposite edge / corner fixed (in local axes)
      const ax = h.includes('e') ? -1 : h.includes('w') ? 1 : 0;
      const ay = h.includes('s') ? -1 : h.includes('n') ? 1 : 0;
      const shiftLx = ax === 0 ? 0 : (-ax * (w - b0.w)) / 2;
      const shiftLy = ay === 0 ? 0 : (-ay * (hh - b0.h)) / 2;
      next = {
        ...b0,
        w,
        h: hh,
        cx: b0.cx + shiftLx * cos - shiftLy * sin,
        cy: b0.cy + shiftLx * sin + shiftLy * cos,
      };
    }
    writeBox(st.sel, next);
    setDrag({ guides });
  };

  const endDrag = (e) => {
    if (!dragRef.current) return;
    try { overlayRef.current?.releasePointerCapture?.(e.pointerId); } catch { /* not captured */ }
    dragRef.current = null;
    setDrag(null);
  };

  useEffect(() => {
    const cancel = () => { dragRef.current = null; setDrag(null); };
    window.addEventListener('blur', cancel);
    return () => window.removeEventListener('blur', cancel);
  }, []);

  const selBox = itemBox(design, selected, W, H);
  const selVisible = selected && items.some((it) => it.kind === selected.kind && it.id === selected.id);
  const selLabel = !selected ? '' : selected.kind === 'layer'
    ? LAYER_META[selected.id]?.label
    : layerLabel(design.decor.find((x) => x.id === selected.id));

  return (
    <div className="ths-stage__wrap" ref={wrapRef}>
      {dims.w > 0 && (
        <div className={`ths-stage ${drag ? 'is-dragging' : ''}`} style={{ width: dims.w, height: dims.h }}>
          <ThCanvasScene design={design} mode={mode} W={W} H={H} dims={dims} media={media} animate={!drag} />

          <div
            ref={overlayRef}
            className="ths-stage__overlay"
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect(null); }}
          >
            {items.map((it) => {
              const b = itemBox(design, it, W, H);
              if (!b) return null;
              const isSel = selected && selected.kind === it.kind && selected.id === it.id;
              return (
                <div
                  key={`${it.kind}-${it.id}`}
                  className={`ths-hit ths-hit--${it.kind === 'layer' ? it.id : 'decor'} ${isSel ? 'is-selected' : ''}`}
                  style={{
                    left: (b.cx - b.w / 2) * s,
                    top: (b.cy - b.h / 2) * s,
                    width: b.w * s,
                    height: b.h * s,
                    transform: b.rot ? `rotate(${b.rot}deg)` : undefined,
                    borderRadius: it.kind === 'layer' && design.layers[it.id]?.frame?.shape === 'circle' ? '50%' : undefined,
                  }}
                  onPointerDown={(e) => startDrag(e, it)}
                  onDoubleClick={() => { if (it.kind === 'decor' && it.layer?.type === 'text') onEditText?.(it.id); }}
                  title={it.kind === 'layer' ? `${LAYER_META[it.id].label} — drag to move` : layerLabel(it.layer)}
                  role="button"
                  aria-label={it.kind === 'layer' ? LAYER_META[it.id].label : layerLabel(it.layer)}
                />
              );
            })}

            {selVisible && selBox && (
              <div
                className={`ths-sel ths-sel--${selected.kind === 'layer' ? selected.id : 'decor'} ${(selBox.cy - selBox.h / 2) * s < 26 ? 'ths-sel--flip' : ''}`}
                style={{
                  left: (selBox.cx - selBox.w / 2) * s,
                  top: (selBox.cy - selBox.h / 2) * s,
                  width: selBox.w * s,
                  height: selBox.h * s,
                  transform: selBox.rot ? `rotate(${selBox.rot}deg)` : undefined,
                }}
              >
                <span className="ths-sel__tag">
                  {selLabel}
                  <em>{Math.round(selBox.w)} × {Math.round(selBox.h)}</em>
                </span>
                {HANDLES.map((h) => (
                  <span
                    key={h}
                    className={`ths-handle ths-handle--${h}`}
                    onPointerDown={(e) => startDrag(e, selected, h)}
                  />
                ))}
              </div>
            )}

            {drag?.guides.x.map((x, i) => <span key={`gx${i}`} className="ths-guide ths-guide--v" style={{ left: x * s }} />)}
            {drag?.guides.y.map((y, i) => <span key={`gy${i}`} className="ths-guide ths-guide--h" style={{ top: y * s }} />)}
          </div>
        </div>
      )}
    </div>
  );
}
